import classNames from "classnames";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import DataTable from "PFComponents/data_table/data_table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./permissions_group_edit_page.module.scss";

type Item = {
  name: string;
  display_as: string;
};

interface IProps {
  items: any[];
  idToPermLevel: Record<number, string>;
  handleChange: (id: number, level: string) => void;
  isRWOnly?: (i: any) => boolean;
  dataTableQAId: string;
  getItemName?: (item: Item) => string;
  getDisplayAs: (item: Item) => string;
}

const PermissionsGroupItemsEdit = ({
  items = [],
  idToPermLevel = {},
  handleChange,
  isRWOnly = () => false,
  dataTableQAId,
  getItemName,
  getDisplayAs
}: IProps) => {
  const { t } = useTranslation();

  const LEVEL_TO_COPY = useMemo(() => ({ ro: t("permissions.read"), rw: t("permissions.readWrite") }), []);

  useEffect(() => {
    items.forEach((i) => {
      const level = idToPermLevel[i.id] || "hidden";
      const newLevel = isRWOnly(i) ? "rw" : level;
      handleChange(i.id, newLevel);
    });
  }, [t]);

  const renderButton = (item, level) => {
    const disabled = isRWOnly(item) && (level === "hidden" || level === "ro");
    return (
      <Button
        small
        kind={level === (idToPermLevel[item.id] || "hidden") ? "secondary" : "primary"}
        onClick={() => handleChange(item.id, level)}
        disabled={disabled}
      >
        {LEVEL_TO_COPY[level] || t("permissions.hidden")}
      </Button>
    );
  };

  const setAll = (level) => {
    items.forEach((i) => {
      const newLevel = isRWOnly(i) ? "rw" : level;
      handleChange(i.id, newLevel);
    });
  };

  return (
    <>
      <div style={{ textAlign: "right", marginBottom: 5 }}>
        <ButtonGroup autoWidth className={classNames(css.buttonGroup, css.header)}>
          <Button small kind="secondary" onClick={() => setAll("hidden")}>
            {t("permissions.allHidden")}
          </Button>
          <Button small kind="secondary" onClick={() => setAll("ro")}>
            {t("permissions.allRead")}
          </Button>
          <Button small kind="secondary" onClick={() => setAll("rw")}>
            {t("permissions.allReadWrite")}
          </Button>
        </ButtonGroup>
      </div>

      <DataTable
        qaId={dataTableQAId}
        data={items}
        columns={[
          {
            renderCell: (item) => (
              <span>
                {getDisplayAs(item)}
                {getItemName && <small style={{ opacity: 0.4, marginLeft: 5 }}>({getItemName(item)})</small>}
              </span>
            )
          },
          {
            narrow: true,
            renderCell: (ct) => (
              <ButtonGroup autoWidth className={css.buttonGroup}>
                {renderButton(ct, "hidden")}
                {renderButton(ct, "ro")}
                {renderButton(ct, "rw")}
              </ButtonGroup>
            )
          }
        ]}
      />
    </>
  );
};

export default PermissionsGroupItemsEdit;
